// frontend/src/pages/ParkingPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { parkingAPI } from '../services/api';
import './ParkingPage.css';

// ─── Tarifas escalonadas por días de estadía (fuente de verdad del frontend) ───
const TARIFAS = [
    { label: '1 – 3',   min: 1,  max: 3,        precio: 12  },
    { label: '4 – 5',   min: 4,  max: 5,        precio: 10  },
    { label: '6 – 9',   min: 6,  max: 9,        precio: 9   },
    { label: '10 – 15', min: 10, max: 15,       precio: 8   },
    { label: '16 – 20', min: 16, max: 20,       precio: 7.5 },
    { label: '21 – 25', min: 21, max: 25,       precio: 7   },
    { label: '26 – 30', min: 26, max: 30,       precio: 6.5 },
    { label: '31+',     min: 31, max: Infinity, precio: 6   },
];
const getTarifaPorDias = (dias) => {
    if (!dias || dias < 1) return 0;
    const tramo = TARIFAS.find(t => dias >= t.min && dias <= t.max);
    return tramo ? tramo.precio : 6;
};

export default function ParkingPage() {
    const navigate = useNavigate();
    const [tab, setTab] = useState('activos'); // 'activos' | 'historial' | 'gastos' | 'cierre'
    const [stats, setStats] = useState({
        vehiculos_en_patio: 0,
        entradas_hoy: 0,
        salidas_hoy: 0,
        recaudado_hoy: 0,
        pagos_desglose_hoy: []
    });
    const [activos, setActivos] = useState([]);
    const [historial, setHistorial] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    // Modales
    const [isCheckInOpen, setIsCheckInOpen] = useState(false);
    const [isCheckOutOpen, setIsCheckOutOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [calcData, setCalcData] = useState(null);
    const [calcLoading, setCalcLoading] = useState(false);

    // Formulario Check-in
    const [checkInForm, setCheckInForm] = useState({
        placa: '',
        cliente_nombre: '',
        telefono: '',
        fecha_ingreso: '',
        fecha_estimada_retorno: '',
        observaciones: ''
    });

    // Formulario Check-out
    const [tarifaDia, setTarifaDia] = useState(0);
    const [esManual, setEsManual] = useState(false);
    const [montoTotalCobrar, setMontoTotalCobrar] = useState(''); // Monto total acordado final
    const [montoYappy, setMontoYappy] = useState('');
    const [montoEfectivo, setMontoEfectivo] = useState('');
    const [obsCheckout, setObsCheckout] = useState('');
    const [metodoPago, setMetodoPago] = useState('EFECTIVO'); // 'EFECTIVO' | 'YAPPY'
    const [showTarifaTable, setShowTarifaTable] = useState(false);
    const [esPagoAnticipado, setEsPagoAnticipado] = useState(false); // TRUE: El auto pagó pero permanece en patio
    const [fechaSalidaManual, setFechaSalidaManual] = useState(''); // Para editar salida manualmente
    const [submitting, setSubmitting] = useState(false);

    // Modal Dar Salida (Vehículos Prepagados)
    const [isDarSalidaOpen, setIsDarSalidaOpen] = useState(false);
    const [ticketToDarSalida, setTicketToDarSalida] = useState(null);
    const [darSalidaObs, setDarSalidaObs] = useState('');
    const [darSalidaSubmitting, setDarSalidaSubmitting] = useState(false);

    // Modales de anulación
    const [isCancelIngresoOpen, setIsCancelIngresoOpen] = useState(false);
    const [ticketToCancel, setTicketToCancel] = useState(null);
    const [cancelIngresoMotivo, setCancelIngresoMotivo] = useState('');

    const [isAnularCobroOpen, setIsAnularCobroOpen] = useState(false);
    const [ticketToAnularCobro, setTicketToAnularCobro] = useState(null);
    const [anularCobroAccion, setAnularCobroAccion] = useState('REABRIR'); // 'REABRIR' | 'DEFINITIVA'
    const [anularCobroMotivo, setAnularCobroMotivo] = useState('');
    const [anularSubmitting, setAnularSubmitting] = useState(false);

    // Modal Editar Ticket (corregir fecha_ingreso / fecha_estimada_retorno)
    const [isEditTicketOpen, setIsEditTicketOpen] = useState(false);
    const [ticketToEdit, setTicketToEdit] = useState(null);
    const [editForm, setEditForm] = useState({ fecha_ingreso: '', fecha_estimada_retorno: '' });
    const [editSubmitting, setEditSubmitting] = useState(false);

    // Gastos de Parking (tabla separada parking_gastos)
    const [gastos, setGastos] = useState([]);
    const [gastoForm, setGastoForm] = useState({ descripcion: '', monto: '', fecha_consumo: new Date().toISOString().split('T')[0] });
    const [gastoSubmitting, setGastoSubmitting] = useState(false);
    const [gastosLoading, setGastosLoading] = useState(false);

    // Cierre Quincenal
    const [cierreQ1, setCierreQ1] = useState(null);
    const [cierreQ2, setCierreQ2] = useState(null);
    const [cierreLoading, setCierreLoading] = useState(false);

    // Cargar datos principales
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsRes, activosRes, histRes] = await Promise.all([
                parkingAPI.getStats(),
                parkingAPI.getActivos(),
                parkingAPI.getHistorial({ limit: 100 })
            ]);
            if (statsRes.data?.success) setStats(statsRes.data.data);
            if (activosRes.data?.success) setActivos(activosRes.data.data);
            if (histRes.data?.success) setHistorial(histRes.data.data);
        } catch (error) {
            console.error('Error al cargar datos de parking:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Cargar gastos del parking
    const loadGastos = useCallback(async (params = {}) => {
        setGastosLoading(true);
        try {
            const res = await parkingAPI.getGastos(params);
            if (res.data?.success) setGastos(res.data.data);
        } catch (error) {
            console.error('Error al cargar gastos:', error);
        } finally {
            setGastosLoading(false);
        }
    }, []);

    // Helper: calcular rango de quincena del mes actual
    const getQuincenaRanges = () => {
        const hoy = new Date();
        const anio = hoy.getFullYear();
        const mes = hoy.getMonth();
        const ultimoDia = new Date(anio, mes + 1, 0).getDate();
        const mesStr = String(mes + 1).padStart(2, '0');
        return {
            q1: { desde: `${anio}-${mesStr}-01`, hasta: `${anio}-${mesStr}-15`, label: `1-15 de ${hoy.toLocaleString('es-PA', { month: 'long' })} ${anio}` },
            q2: { desde: `${anio}-${mesStr}-16`, hasta: `${anio}-${mesStr}-${ultimoDia}`, label: `16-${ultimoDia} de ${hoy.toLocaleString('es-PA', { month: 'long' })} ${anio}` }
        };
    };

    // Cargar cierre quincenal
    const loadCierreQuincenal = useCallback(async () => {
        setCierreLoading(true);
        try {
            const { q1, q2 } = getQuincenaRanges();
            const [resQ1Stats, resQ2Stats, resQ1Gastos, resQ2Gastos] = await Promise.all([
                parkingAPI.getStats({ period: 'custom', startDate: q1.desde, endDate: q1.hasta }),
                parkingAPI.getStats({ period: 'custom', startDate: q2.desde, endDate: q2.hasta }),
                parkingAPI.getGastos({ fecha_desde: q1.desde, fecha_hasta: q1.hasta }),
                parkingAPI.getGastos({ fecha_desde: q2.desde, fecha_hasta: q2.hasta })
            ]);
            if (resQ1Stats.data?.success) {
                const d = resQ1Stats.data.data;
                setCierreQ1({ ...d, ...q1, gastos_total: resQ1Gastos.data?.total || 0, gastos_detalle: resQ1Gastos.data?.data || [] });
            }
            if (resQ2Stats.data?.success) {
                const d = resQ2Stats.data.data;
                setCierreQ2({ ...d, ...q2, gastos_total: resQ2Gastos.data?.total || 0, gastos_detalle: resQ2Gastos.data?.data || [] });
            }
        } catch (error) {
            console.error('Error al cargar cierre quincenal:', error);
        } finally {
            setCierreLoading(false);
        }
    }, []); // eslint-disable-line


    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 45000);
        return () => clearInterval(interval);
    }, [loadData]);

    // Manejo de Check-In
    const handleCheckInSubmit = async (e) => {
        e.preventDefault();
        if (!checkInForm.placa || !checkInForm.cliente_nombre) {
            alert('La Placa y el Nombre del cliente son requeridos');
            return;
        }
        setSubmitting(true);
        try {
            const res = await parkingAPI.checkIn(checkInForm);
            if (res.data?.success) {
                setIsCheckInOpen(false);
                setCheckInForm({
                    placa: '',
                    cliente_nombre: '',
                    telefono: '',
                    fecha_ingreso: '',
                    fecha_estimada_retorno: '',
                    observaciones: ''
                });
                await loadData();
            }
        } catch (error) {
            alert(error.response?.data?.message || 'Error al registrar vehículo');
        } finally {
            setSubmitting(false);
        }
    };

    // Abrir Modal Check-Out
    const handleOpenCheckout = async (ticket) => {
        setSelectedTicket(ticket);
        setIsCheckOutOpen(true);
        setCalcLoading(true);
        setEsPagoAnticipado(false);
        setFechaSalidaManual('');
        await fetchCalculoCheckout(ticket.id, null);
    };

    const fetchCalculoCheckout = async (ticketId, fechaSalidaParam) => {
        setCalcLoading(true);
        try {
            const urlParams = fechaSalidaParam ? { fecha_salida: fechaSalidaParam } : {};
            const res = await parkingAPI.calcularCheckout(ticketId, urlParams);
            if (res.data?.success) {
                const data = res.data.data;
                setCalcData(data);
                if (!fechaSalidaParam) {
                    // Convertir UTC → hora Panamá para el input datetime-local
                    const utcDate = new Date(data.fecha_salida_calculada);
                    const pad = (n) => String(n).padStart(2, '0');
                    // Panamá es UTC-5, sin horario de verano
                    const panamaOffset = -5 * 60; // minutos
                    const localMs = utcDate.getTime() + (panamaOffset - (-utcDate.getTimezoneOffset())) * 60000;
                    const localDate = new Date(localMs);
                    const localStr = `${localDate.getFullYear()}-${pad(localDate.getMonth()+1)}-${pad(localDate.getDate())}T${pad(localDate.getHours())}:${pad(localDate.getMinutes())}`;
                    setFechaSalidaManual(localStr);
                }
                const diasACobrar = data.dias_a_cobrar || 0;
                const tarifaFrontend = getTarifaPorDias(diasACobrar);
                const montoCalculado = parseFloat((diasACobrar * tarifaFrontend).toFixed(2));
                setTarifaDia(tarifaFrontend);
                setEsManual(false);
                const inicialMonto = montoCalculado > 0 ? montoCalculado.toFixed(2) : '';
                setMontoTotalCobrar(inicialMonto);
                setMontoEfectivo(inicialMonto);
                setMontoYappy('');
                setMetodoPago('EFECTIVO');
                setObsCheckout('');
            }
        } catch (error) {
            alert('Error al calcular estadía');
            setIsCheckOutOpen(false);
        } finally {
            setCalcLoading(false);
        }
    };

    // currentDias y tarifaAplicada usan las constantes de módulo (TARIFAS / getTarifaPorDias)
    const currentDias = calcData?.dias_a_cobrar || 0;
    const tarifaAplicada = getTarifaPorDias(currentDias);
    const totalCalculado = parseFloat((currentDias * tarifaAplicada).toFixed(2));

    const llenarTodoYappy = () => {
        const total = parseFloat(montoTotalCobrar) || 0;
        setMontoYappy(total > 0 ? total.toFixed(2) : '');
        setMontoEfectivo('');
    };

    const llenarTodoEfectivo = () => {
        const total = parseFloat(montoTotalCobrar) || 0;
        setMontoEfectivo(total > 0 ? total.toFixed(2) : '');
        setMontoYappy('');
    };

    // Confirmar Cobro Check-Out (Salida Normal o Prepago en Patio)
    const handleConfirmCheckout = async (e) => {
        e.preventDefault();
        const yappy = parseFloat(montoYappy) || 0;
        const efectivo = parseFloat(montoEfectivo) || 0;
        const totalPagado = yappy + efectivo;
        const totalFinalCobrar = parseFloat(montoTotalCobrar) || 0;

        if (totalFinalCobrar <= 0) {
            alert('Debe ingresar un monto total a cobrar mayor a $0.00');
            return;
        }

        if (totalPagado < totalFinalCobrar - 0.05) {
            alert(`El total ingresado en formas de pago ($${totalPagado.toFixed(2)}) no cubre el monto a cobrar acordado ($${totalFinalCobrar.toFixed(2)})`);
            return;
        }

        const pagos = [];
        if (yappy > 0) {
            pagos.push({ metodo_pago: 'YAPPY', monto: yappy });
        }
        if (efectivo > 0) {
            pagos.push({ metodo_pago: 'EFECTIVO', monto: efectivo });
        }

        setSubmitting(true);
        try {
            const res = await parkingAPI.confirmarCheckout(selectedTicket.id, {
                fecha_salida: fechaSalidaManual ? new Date(fechaSalidaManual).toISOString() : null,
                dias_cobrados: currentDias,
                tarifa_dia_aplicada: parseFloat(tarifaDia),
                es_tarifa_manual: Math.abs(totalFinalCobrar - totalCalculado) > 0.01,
                monto_total: totalFinalCobrar, // Monto real acordado
                pagos,
                es_pago_anticipado: esPagoAnticipado,
                observaciones: obsCheckout
            });
            if (res.data?.success) {
                setIsCheckOutOpen(false);
                setSelectedTicket(null);
                setCalcData(null);
                await loadData();
            }
        } catch (error) {
            alert(error.response?.data?.message || 'Error al procesar salida y cobro');
        } finally {
            setSubmitting(false);
        }
    };

    // ── GESTIÓN DE SALIDA DIRECTA (VEHÍCULOS PREPAGADOS EN PATIO) ──
    const handleOpenDarSalida = (ticket) => {
        setTicketToDarSalida(ticket);
        setDarSalidaObs('');
        setIsDarSalidaOpen(true);
    };

    const handleConfirmDarSalida = async (e) => {
        e.preventDefault();
        if (!ticketToDarSalida) return;
        try {
            const res = await parkingAPI.darSalidaPrepago(ticketToDarSalida.id, {
                observaciones: darSalidaObs
            });
            if (res.data?.success) {
                setIsDarSalidaOpen(false);
                setTicketToDarSalida(null);
                await loadData();
            }
        } catch (error) {
            alert(error.response?.data?.message || 'Error al registrar salida del vehículo');
        } finally {
            setDarSalidaSubmitting(false);
        }
    };

    // ── GESTIÓN DE ANULACIÓN DE INGRESO (CHECK-IN) ──
    const handleOpenCancelIngreso = (ticket) => {
        setTicketToCancel(ticket);
        setCancelIngresoMotivo('');
        setIsCancelIngresoOpen(true);
    };

    const handleConfirmCancelIngreso = async (e) => {
        e.preventDefault();
        if (!ticketToCancel) return;
        setAnularSubmitting(true);
        try {
            const res = await parkingAPI.anularIngreso(ticketToCancel.id, {
                motivo: cancelIngresoMotivo
            });
            if (res.data?.success) {
                setIsCancelIngresoOpen(false);
                setTicketToCancel(null);
                await loadData();
            }
        } catch (error) {
            alert(error.response?.data?.message || 'Error al cancelar ingreso');
        } finally {
            setAnularSubmitting(false);
        }
    };

    // ── GESTIÓN DE EDICIÓN DE TICKET (FECHA INGRESO / RETORNO) ──
    const handleOpenEditTicket = (ticket) => {
        setTicketToEdit(ticket);
        // Formatear fecha_ingreso al formato datetime-local (YYYY-MM-DDTHH:mm)
        const fi = ticket.fecha_ingreso ? new Date(ticket.fecha_ingreso) : new Date();
        const fer = ticket.fecha_estimada_retorno ? new Date(ticket.fecha_estimada_retorno) : null;
        const toLocal = (d) => {
            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        };
        setEditForm({
            fecha_ingreso: toLocal(fi),
            fecha_estimada_retorno: fer ? fer.toISOString().split('T')[0] : ''
        });
        setIsEditTicketOpen(true);
    };

    const handleConfirmEditTicket = async (e) => {
        e.preventDefault();
        if (!ticketToEdit || !editForm.fecha_ingreso) return;
        setEditSubmitting(true);
        try {
            const res = await parkingAPI.editarTicket(ticketToEdit.id, {
                fecha_ingreso: new Date(editForm.fecha_ingreso).toISOString(),
                fecha_estimada_retorno: editForm.fecha_estimada_retorno || null
            });
            if (res.data?.success) {
                setIsEditTicketOpen(false);
                setTicketToEdit(null);
                await loadData();
            }
        } catch (error) {
            alert(error.response?.data?.message || 'Error al editar el ticket');
        } finally {
            setEditSubmitting(false);
        }
    };

    // ── GESTIÓN DE GASTOS DE PARKING ──
    const handleCreateGasto = async (e) => {
        e.preventDefault();
        if (!gastoForm.descripcion || !gastoForm.monto) return;
        setGastoSubmitting(true);
        try {
            const res = await parkingAPI.createGasto({
                descripcion: gastoForm.descripcion,
                monto: parseFloat(gastoForm.monto),
                fecha_consumo: gastoForm.fecha_consumo || new Date().toISOString().split('T')[0]
            });
            if (res.data?.success) {
                setGastoForm({ descripcion: '', monto: '', fecha_consumo: new Date().toISOString().split('T')[0] });
                await loadGastos();
            }
        } catch (error) {
            alert(error.response?.data?.message || 'Error al registrar gasto');
        } finally {
            setGastoSubmitting(false);
        }
    };

    const handleDeleteGasto = async (id) => {
        if (!window.confirm('¿Eliminar este gasto?')) return;
        try {
            await parkingAPI.deleteGasto(id);
            await loadGastos();
        } catch (error) {
            alert('Error al eliminar el gasto');
        }
    };

    // ── GESTIÓN DE CIERRE QUINCENAL ──
    const handleTabChange = (newTab) => {
        setTab(newTab);
        if (newTab === 'gastos') loadGastos();
        if (newTab === 'cierre') loadCierreQuincenal();
    };

    // ── GESTIÓN DE ANULACIÓN DE COBRO (CHECK-OUT) ──
    const handleOpenAnularCobro = (ticket) => {
        setTicketToAnularCobro(ticket);
        setAnularCobroAccion('REABRIR');
        setAnularCobroMotivo('');
        setIsAnularCobroOpen(true);
    };

    const handleConfirmAnularCobro = async (e) => {
        e.preventDefault();
        if (!ticketToAnularCobro) return;
        if (!anularCobroMotivo.trim()) {
            alert('Por favor detalle el motivo de la anulación.');
            return;
        }
        setAnularSubmitting(true);
        try {
            const res = await parkingAPI.anularCobro(ticketToAnularCobro.id, {
                accion: anularCobroAccion,
                motivo: anularCobroMotivo
            });
            if (res.data?.success) {
                setIsAnularCobroOpen(false);
                setTicketToAnularCobro(null);
                await loadData();
            }
        } catch (error) {
            alert(error.response?.data?.message || 'Error al anular cobro');
        } finally {
            setAnularSubmitting(false);
        }
    };

    // Exportar a Excel Profesional (.xlsx con formato, colores y estilos del cliente)
    const exportarExcel = async () => {
        if (!historial || historial.length === 0) {
            alert('No hay registros en el historial para exportar.');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'ERP Lobos Parking';
        workbook.created = new Date();
        const sheet = workbook.addWorksheet('REGISTRO', {
            views: [{ showGridLines: true }]
        });

        // ── 1. TÍTULO PRINCIPAL (BANNER SUPERIOR) ──
        sheet.mergeCells('A1:Q2');
        const titleCell = sheet.getCell('A1');
        titleCell.value = 'LOBOS PARKING - REGISTRO DE CONTROL DE ESTADÍAS Y COBROS (PANAMÁ)';
        titleCell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        titleCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF0F172A' } // Slate oscuro
        };

        // ── 2. METADATA ──
        sheet.mergeCells('A3:E3');
        const metaCell = sheet.getCell('A3');
        metaCell.value = `Fecha de reporte: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()} | Sucursal: Aeropuerto`;
        metaCell.font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF64748B' } };

        // ── 3. ENCABEZADOS DE COLUMNA ──
        const headers = [
            'Nº',
            'NOMBRES Y APELLIDOS',
            'TELÉFONO',
            'PLACA',
            'Fecha de entrada',
            'Hora de entrada',
            'Fecha de salida',
            'Hora de salida',
            'Nº DE DÍAS',
            'N DIA FINAL\n(Pasando 8 hr es un dia mas)',
            'Precio por dia ($)',
            'Total ($)',
            'PAGO YAPPY',
            'MONTO YAPPY',
            'PAGO EFECTIVO',
            'MONTO EFECTIVO',
            'OBSERVACIONES'
        ];

        const headerRow = sheet.addRow(headers);
        headerRow.height = 34;

        headerRow.eachCell((cell, colNumber) => {
            cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF1E293B' } // Carbón oscuro corporativo
            };
            cell.border = {
                top: { style: 'medium', color: { argb: 'FF0F172A' } },
                bottom: { style: 'medium', color: { argb: 'FF0F172A' } },
                left: { style: 'thin', color: { argb: 'FF334155' } },
                right: { style: 'thin', color: { argb: 'FF334155' } }
            };

            // Resaltar encabezado de Total ($) con dorado
            if (colNumber === 12) {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFD97706' } // Ámbar oscuro
                };
            }
        });

        // ── 4. FILAS DE DATOS ──
        let sumaTotal = 0;
        let sumaYappy = 0;
        let sumaEfectivo = 0;

        historial.forEach((t, index) => {
            const fIngreso = new Date(t.fecha_ingreso);
            const fSalida = t.fecha_salida ? new Date(t.fecha_salida) : null;
            const esCobrado = t.estado === 'FINALIZADO';
            const yappyMonto = esCobrado 
                ? (t.pagos || []).filter(p => p.metodo_pago === 'YAPPY').reduce((acc, p) => acc + (parseFloat(p.monto) || 0), 0)
                : 0;
            const efectivoMonto = esCobrado 
                ? (t.pagos || []).filter(p => p.metodo_pago === 'EFECTIVO').reduce((acc, p) => acc + (parseFloat(p.monto) || 0), 0)
                : 0;
            const totalPagos = yappyMonto + efectivoMonto;
            const totalGuardado = parseFloat(t.monto_total || 0);
            
            // Total real: si tiene monto_total lo usa, o la suma real de pagos recibidos
            const total = esCobrado ? (totalGuardado > 0 ? totalGuardado : totalPagos) : 0;

            if (esCobrado) {
                sumaTotal += total;
                sumaYappy += yappyMonto;
                sumaEfectivo += efectivoMonto;
            }

            // Calcular texto de días y horas
            const diasTexto = t.tiempo_transcurrido_texto || `${t.dias_cobrados || 1} Dias`;

            // Observaciones enriquecidas con anulación o prepago si aplica
            const obsTexto = t.estado === 'ANULADO' 
                ? `[ANULADO: ${t.motivo_anulacion || 'Sin motivo'}] ${t.observaciones || ''}` 
                : t.estado === 'CANCELADO'
                ? `[CANCELADO: ${t.motivo_anulacion || 'Sin motivo'}] ${t.observaciones || ''}`
                : t.es_prepago && t.estado === 'ACTIVO'
                ? `[PAGADO EN PATIO - PREPAGO] ${t.observaciones || ''}`
                : (t.observaciones || '');

            const rowData = [
                String(index + 1).padStart(4, '0'),
                t.cliente_nombre || '',
                t.telefono || '',
                t.placa || '',
                fIngreso.toLocaleDateString('es-PA'),
                fIngreso.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
                fSalida ? fSalida.toLocaleDateString('es-PA') : '',
                fSalida ? fSalida.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '',
                diasTexto,
                parseFloat(t.dias_cobrados || 1),
                parseFloat(t.tarifa_dia_aplicada || 9),
                total,
                yappyMonto > 0 ? 'SI' : 'NO',
                yappyMonto > 0 ? yappyMonto : '',
                efectivoMonto > 0 ? 'SI' : 'NO',
                efectivoMonto > 0 ? efectivoMonto : '',
                obsTexto
            ];

            const row = sheet.addRow(rowData);
            row.height = 22;

            row.eachCell((cell, colNumber) => {
                cell.font = { name: 'Calibri', size: 10 };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                    bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                    left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
                    right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
                };

                // Alineaciones
                if ([1, 4, 5, 6, 7, 8, 9, 10, 13, 15].includes(colNumber)) {
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                } else if ([11, 12, 14, 16].includes(colNumber)) {
                    cell.alignment = { horizontal: 'right', vertical: 'middle' };
                    cell.numFmt = '$#,##0.00';
                } else {
                    cell.alignment = { horizontal: 'left', vertical: 'middle' };
                }

                // Columna Total ($) - Fondo amarillo pastel como en el Excel original
                if (colNumber === 12) {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFFFF2CC' } // Dorado pastel idéntico a Excel original
                    };
                    cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF92400E' } };
                }

                // Placa con negrita
                if (colNumber === 4) {
                    cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF0F172A' } };
                }
            });
        });

        // ── 5. FILA DE TOTALES GENERALES ──
        sheet.addRow([]);
        const totalRowData = [
            '',
            'TOTALES GENERALES',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            sumaTotal,
            '',
            sumaYappy,
            '',
            sumaEfectivo,
            ''
        ];
        const totalRow = sheet.addRow(totalRowData);
        totalRow.height = 26;

        totalRow.eachCell((cell, colNumber) => {
            cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF0F172A' } };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF1F5F9' }
            };
            cell.border = {
                top: { style: 'thin', color: { argb: 'FF94A3B8' } },
                bottom: { style: 'double', color: { argb: 'FF0F172A' } }
            };

            if ([12, 14, 16].includes(colNumber)) {
                cell.numFmt = '$#,##0.00';
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
            }
            if (colNumber === 12) {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFFFE599' }
                };
            }
        });

        // ── 6. ANCHOS DE COLUMNA AUTOMÁTICOS Y PERFECTOS ──
        const colWidths = [
            8,   // Nº
            28,  // Nombres
            16,  // Teléfono
            14,  // Placa
            16,  // Fecha Entrada
            16,  // Hora Entrada
            16,  // Fecha Salida
            16,  // Hora Salida
            16,  // Nº Días
            18,  // N Día Final
            18,  // Precio/Día
            18,  // Total ($)
            14,  // Pago Yappy
            16,  // Monto Yappy
            16,  // Pago Efectivo
            18,  // Monto Efectivo
            30   // Observaciones
        ];

        colWidths.forEach((w, idx) => {
            sheet.getColumn(idx + 1).width = w;
        });

        // ── 7. GENERAR BUFFER Y DESCARGAR .XLSX NATIVO ──
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { 
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `Lobos_Parking_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
    };

    const activosFiltrados = activos.filter(t => 
        t.placa.toLowerCase().includes(search.toLowerCase()) ||
        t.cliente_nombre.toLowerCase().includes(search.toLowerCase())
    );

    const historialFiltrado = historial.filter(t =>
        t.placa.toLowerCase().includes(search.toLowerCase()) ||
        t.cliente_nombre.toLowerCase().includes(search.toLowerCase()) ||
        t.codigo_ticket.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="parking-container">
            {/* Header */}
            <div className="parking-header">
                <div className="parking-title-box">
                    <div className="parking-header-tag">
                        <span>✈️ Aeropuerto Tocumen / Panamá</span>
                    </div>
                    <h1>🚗 Control de Estacionamiento</h1>
                    <p className="parking-subtitle">
                        Cobro automatizado por estadía diaria • Tolerancia de 6 horas • Yappy & Efectivo USD
                    </p>
                </div>
                <div className="parking-header-actions">
                    <button className="btn-secondary-action" onClick={() => navigate('/dashboard')} title="Ver cierre quincenal, recaudación y balance de caja" style={{ border: '1px solid #0284c7', color: '#0284c7' }}>
                        📊 Cierre Quincenal & Caja
                    </button>
                    <button className="btn-secondary-action" onClick={exportarExcel} title="Descargar reporte en formato Excel">
                        📥 Exportar a Excel
                    </button>
                    <button className="btn-primary-action" onClick={() => setIsCheckInOpen(true)}>
                        <span>➕</span> Nuevo Ingreso (Check-in)
                    </button>
                </div>
            </div>

            {/* KPIs */}
            <div className="parking-kpis">
                <div className="parking-card card-blue">
                    <div className="card-icon-wrapper icon-blue">🚗</div>
                    <div className="card-info">
                        <span className="card-label">En Patio Ahora</span>
                        <span className="card-value">{stats.vehiculos_en_patio}</span>
                        <span className="card-subtext">Vehículos en viaje</span>
                    </div>
                </div>
                <div className="parking-card card-green">
                    <div className="card-icon-wrapper icon-green">📥</div>
                    <div className="card-info">
                        <span className="card-label">Entradas Hoy</span>
                        <span className="card-value">{stats.entradas_hoy}</span>
                        <span className="card-subtext">Check-ins registrados</span>
                    </div>
                </div>
                <div className="parking-card card-amber">
                    <div className="card-icon-wrapper icon-amber">📤</div>
                    <div className="card-info">
                        <span className="card-label">Salidas Hoy</span>
                        <span className="card-value">{stats.salidas_hoy}</span>
                        <span className="card-subtext">Check-outs cobrados</span>
                    </div>
                </div>
                <div className="parking-card card-purple">
                    <div className="card-icon-wrapper icon-purple">💵</div>
                    <div className="card-info">
                        <span className="card-label">Recaudado Hoy</span>
                        <span className="card-value">${parseFloat(stats.recaudado_hoy || 0).toFixed(2)}</span>
                        <span className="card-subtext">
                            {(stats.pagos_desglose_hoy || []).map(p => `${p.metodo_pago}: $${parseFloat(p.total).toFixed(2)}`).join(' • ') || 'Dólares (USD)'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="parking-toolbar">
                <div className="parking-tabs">
                    <button 
                        className={`parking-tab-btn ${tab === 'activos' ? 'active' : ''}`}
                        onClick={() => handleTabChange('activos')}
                    >
                        🅿️ En Patio
                        <span className="badge-counter">{activos.length}</span>
                    </button>
                    <button 
                        className={`parking-tab-btn ${tab === 'historial' ? 'active' : ''}`}
                        onClick={() => handleTabChange('historial')}
                    >
                        📜 Historial y Cierres
                    </button>
                    <button 
                        className={`parking-tab-btn ${tab === 'gastos' ? 'active' : ''}`}
                        onClick={() => handleTabChange('gastos')}
                    >
                        💰 Gastos
                    </button>
                    <button 
                        className={`parking-tab-btn ${tab === 'cierre' ? 'active' : ''}`}
                        onClick={() => handleTabChange('cierre')}
                    >
                        📊 Cierre Quincenal
                    </button>
                </div>
                <div className="search-box-wrapper">
                    <span className="search-icon-inside">🔍</span>
                    <input 
                        type="text" 
                        className="parking-search-input"
                        placeholder="Buscar por placa o cliente..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* Tabla de Activos */}
            {tab === 'activos' && (
                <div className="parking-table-wrapper">
                    <table className="parking-table">
                        <thead>
                            <tr>
                                <th>Ticket</th>
                                <th>Placa</th>
                                <th>Cliente</th>
                                <th>WhatsApp / Celular</th>
                                <th>Fecha Entrada</th>
                                <th>Retorno Estimado</th>
                                <th>Tiempo en Patio</th>
                                <th>Estado de Pago</th>
                                <th style={{ textAlign: 'right' }}>Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="9" style={{ textAlign: 'center', padding: '40px' }}>Actualizando patio...</td></tr>
                            ) : activosFiltrados.length === 0 ? (
                                <tr><td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No hay vehículos activos en el patio en este momento.</td></tr>
                            ) : (
                                activosFiltrados.map(ticket => (
                                    <tr key={ticket.id}>
                                        <td style={{ fontWeight: 600, color: '#64748b' }}>{ticket.codigo_ticket}</td>
                                        <td>
                                            <div className="license-plate-tag">
                                                <span className="plate-header-text">PANAMÁ</span>
                                                <span className="plate-number-text">{ticket.placa}</span>
                                            </div>
                                        </td>
                                        <td style={{ fontWeight: 700 }}>{ticket.cliente_nombre}</td>
                                        <td>
                                            {ticket.telefono ? (
                                                <a 
                                                    href={`https://wa.me/${ticket.telefono.replace(/[^0-9]/g, '')}`} 
                                                    target="_blank" 
                                                    rel="noreferrer"
                                                    className="whatsapp-link"
                                                >
                                                    💬 {ticket.telefono}
                                                </a>
                                            ) : '-'}
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{new Date(ticket.fecha_ingreso).toLocaleDateString()}</div>
                                            <div style={{ fontSize: '11px', color: '#64748b' }}>
                                                {new Date(ticket.fecha_ingreso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </td>
                                        <td>
                                            {ticket.fecha_estimada_retorno ? (
                                                <div style={{ fontWeight: 500 }}>{new Date(ticket.fecha_estimada_retorno).toLocaleDateString()}</div>
                                            ) : (
                                                <span style={{ color: '#94a3b8', fontSize: '12px' }}>Sin fecha fija</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="time-counter-badge">
                                                <span className="time-pulse-dot"></span>
                                                <span>{ticket.tiempo_transcurrido_texto || `${ticket.dias_calculados_hoy || 1}d`}</span>
                                            </div>
                                        </td>
                                        <td>
                                            {ticket.es_prepago ? (
                                                <div>
                                                    <span className="tag-status-badge tag-status-pagado-patio">
                                                        🟢 Pagado (${parseFloat(ticket.total_pagado || ticket.monto_total || 0).toFixed(2)})
                                                    </span>
                                                    {ticket.dias_cobrados && (
                                                        <div style={{ fontSize: '11px', color: '#059669', fontWeight: 600, marginTop: '2px' }}>
                                                            {ticket.dias_cobrados}d prepagados
                                                        </div>
                                                    )}
                                                    {ticket.dias_excedentes > 0 && (
                                                        <div className="badge-dias-excedentes" title={`Superó en ${ticket.dias_excedentes} día(s) lo prepagado`}>
                                                            ⚠️ +{ticket.dias_excedentes}d extra
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="tag-status-badge tag-status-pendiente">
                                                    ⏳ Pendiente
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <div style={{ display: 'inline-flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                {/* ✏️ Editar fecha de ingreso/retorno */}
                                                <button
                                                    className="btn-secondary-action"
                                                    style={{ padding: '5px 9px', fontSize: '11px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}
                                                    onClick={() => handleOpenEditTicket(ticket)}
                                                    title="Editar fecha de ingreso o retorno estimado"
                                                >
                                                    ✏️ Editar
                                                </button>
                                                {ticket.es_prepago ? (
                                                    <>
                                                        <button 
                                                            className="btn-dar-salida-action"
                                                            onClick={() => handleOpenDarSalida(ticket)}
                                                            title="El cliente ya pagó previamente. Confirmar salida del patio."
                                                        >
                                                            🚪 Dar Salida
                                                        </button>
                                                        {ticket.dias_excedentes > 0 ? (
                                                            <button 
                                                                className="btn-checkout-action"
                                                                style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
                                                                onClick={() => handleOpenCheckout(ticket)}
                                                                title="Cobrar días excedentes"
                                                            >
                                                                💳 Extra
                                                            </button>
                                                        ) : (
                                                            <button 
                                                                className="btn-cancel-ingreso-action"
                                                                onClick={() => handleOpenAnularCobro(ticket)}
                                                                title="Anular cobro o reabrir estadía"
                                                            >
                                                                🚫 Anular
                                                            </button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <button 
                                                            className="btn-checkout-action"
                                                            onClick={() => handleOpenCheckout(ticket)}
                                                            title="Cobrar estadía o registrar pago anticipado"
                                                        >
                                                            💳 Cobrar / Salida
                                                        </button>
                                                        <button 
                                                            className="btn-cancel-ingreso-action"
                                                            onClick={() => handleOpenCancelIngreso(ticket)}
                                                            title="Cancelar ingreso (vehículo no ingresó o error de placa)"
                                                        >
                                                            ✕ Cancelar
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Tabla de Historial */}
            {tab === 'historial' && (
                <div className="parking-table-wrapper">
                    <table className="parking-table">
                        <thead>
                            <tr>
                                <th>Ticket</th>
                                <th>Placa</th>
                                <th>Cliente</th>
                                <th>Entrada</th>
                                <th>Salida</th>
                                <th>Días Cobrados</th>
                                <th>Tarifa/Día</th>
                                <th>Total Pagado</th>
                                <th>Método(s)</th>
                                <th style={{ textAlign: 'center' }}>Estado</th>
                                <th style={{ textAlign: 'right' }}>Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="11" style={{ textAlign: 'center', padding: '40px' }}>Cargando historial...</td></tr>
                            ) : historialFiltrado.length === 0 ? (
                                <tr><td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No se registran tickets en el historial.</td></tr>
                            ) : (
                                historialFiltrado.map(ticket => (
                                    <tr key={ticket.id}>
                                        <td style={{ fontWeight: 600, color: '#64748b' }}>{ticket.codigo_ticket}</td>
                                        <td>
                                            <div className="license-plate-tag">
                                                <span className="plate-header-text">PANAMÁ</span>
                                                <span className="plate-number-text">{ticket.placa}</span>
                                            </div>
                                        </td>
                                        <td style={{ fontWeight: 600 }}>{ticket.cliente_nombre}</td>
                                        <td>
                                            <div>{new Date(ticket.fecha_ingreso).toLocaleDateString()}</div>
                                            <div style={{ fontSize: '11px', color: '#64748b' }}>
                                                {new Date(ticket.fecha_ingreso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </td>
                                        <td>
                                            {ticket.fecha_salida ? (
                                                <>
                                                <div>{new Date(ticket.fecha_salida).toLocaleDateString()}</div>
                                                <div style={{ fontSize: '11px', color: '#64748b' }}>
                                                    {new Date(ticket.fecha_salida).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                                </>
                                            ) : '-'}
                                        </td>
                                        <td style={{ fontWeight: 700 }}>
                                            {ticket.dias_cobrados ? `${ticket.dias_cobrados} días` : '-'}
                                        </td>
                                        <td>
                                            {ticket.tarifa_dia_aplicada ? (
                                                <>
                                                    ${parseFloat(ticket.tarifa_dia_aplicada).toFixed(2)}
                                                    {ticket.es_tarifa_manual && (
                                                        <span style={{ fontSize: '10px', color: '#d97706', marginLeft: '4px', fontWeight: 800 }}>
                                                            (MANUAL)
                                                        </span>
                                                    )}
                                                </>
                                            ) : '-'}
                                        </td>
                                        <td style={{ fontWeight: 800, color: ticket.estado === 'FINALIZADO' ? '#059669' : '#94a3b8', fontSize: '15px' }}>
                                            ${parseFloat(ticket.monto_total || 0).toFixed(2)} USD
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                                {(ticket.pagos || []).map((p, i) => (
                                                    <span 
                                                        key={i} 
                                                        className={p.metodo_pago === 'YAPPY' ? 'tag-yappy-badge' : 'tag-cash-badge'}
                                                    >
                                                        {p.metodo_pago}: ${parseFloat(p.monto).toFixed(2)}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {ticket.estado === 'FINALIZADO' ? (
                                                <span className="tag-status-badge tag-status-finalizado">✓ Cobrado</span>
                                            ) : ticket.es_prepago && ticket.estado === 'ACTIVO' ? (
                                                <span className="tag-status-badge tag-status-pagado-patio">🟢 Prepagado</span>
                                            ) : ticket.estado === 'CANCELADO' ? (
                                                <span className="tag-status-badge tag-status-cancelado" title={ticket.motivo_anulacion || 'Ingreso cancelado'}>✕ Cancelado</span>
                                            ) : ticket.estado === 'ANULADO' ? (
                                                <span className="tag-status-badge tag-status-anulado" title={ticket.motivo_anulacion || 'Cobro anulado'}>🚫 Anulado</span>
                                            ) : (
                                                <span className="tag-status-badge">{ticket.estado}</span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            {ticket.estado === 'FINALIZADO' ? (
                                                <button 
                                                    className="btn-anular-cobro-action"
                                                    onClick={() => handleOpenAnularCobro(ticket)}
                                                    title="Anular o revertir este cobro"
                                                >
                                                    🚫 Anular Cobro
                                                </button>
                                            ) : ticket.es_prepago && ticket.estado === 'ACTIVO' ? (
                                                <div style={{ display: 'inline-flex', gap: '6px' }}>
                                                    <button 
                                                        className="btn-dar-salida-action"
                                                        style={{ padding: '6px 12px', fontSize: '11px' }}
                                                        onClick={() => handleOpenDarSalida(ticket)}
                                                        title="Dar salida a vehículo"
                                                    >
                                                        🚪 Salida
                                                    </button>
                                                    <button 
                                                        className="btn-anular-cobro-action"
                                                        style={{ padding: '6px 10px', fontSize: '11px' }}
                                                        onClick={() => handleOpenAnularCobro(ticket)}
                                                        title="Anular cobro"
                                                    >
                                                        🚫 Anular
                                                    </button>
                                                </div>
                                            ) : (
                                                <span style={{ fontSize: '11px', color: '#94a3b8' }} title={ticket.motivo_anulacion}>
                                                    {ticket.motivo_anulacion ? (ticket.motivo_anulacion.length > 18 ? ticket.motivo_anulacion.substring(0, 18) + '...' : ticket.motivo_anulacion) : '-'}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ═════════════════════════════════════════════════
                TAB: GASTOS DE PARKING
                ═════════════════════════════════════════════════ */}
            {tab === 'gastos' && (
                <div style={{ padding: '20px 0' }}>
                    {/* Formulario nuevo gasto */}
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
                        <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>➕ Registrar Gasto Operativo</h3>
                        <form onSubmit={handleCreateGasto} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div style={{ flex: '2', minWidth: '200px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Concepto *</label>
                                <input
                                    type="text"
                                    className="form-field-input"
                                    placeholder="Ej: Combustible, Mantenimiento, Limpieza..."
                                    value={gastoForm.descripcion}
                                    onChange={(e) => setGastoForm({ ...gastoForm, descripcion: e.target.value })}
                                    required
                                />
                            </div>
                            <div style={{ flex: '1', minWidth: '130px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Monto ($ USD) *</label>
                                <input
                                    type="number"
                                    className="form-field-input"
                                    placeholder="0.00"
                                    min="0.01" step="0.01"
                                    value={gastoForm.monto}
                                    onChange={(e) => setGastoForm({ ...gastoForm, monto: e.target.value })}
                                    required
                                />
                            </div>
                            <div style={{ flex: '1', minWidth: '150px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Fecha de Consumo *</label>
                                <input
                                    type="date"
                                    className="form-field-input"
                                    value={gastoForm.fecha_consumo}
                                    onChange={(e) => setGastoForm({ ...gastoForm, fecha_consumo: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <button type="submit" className="btn-primary-action" disabled={gastoSubmitting} style={{ whiteSpace: 'nowrap' }}>
                                    {gastoSubmitting ? 'Guardando...' : '💾 Guardar Gasto'}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Lista de gastos */}
                    <div className="parking-table-wrapper">
                        <table className="parking-table">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Concepto</th>
                                    <th style={{ textAlign: 'right' }}>Monto (USD)</th>
                                    <th style={{ textAlign: 'center' }}>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {gastosLoading ? (
                                    <tr><td colSpan="4" style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>Cargando gastos...</td></tr>
                                ) : gastos.length === 0 ? (
                                    <tr><td colSpan="4" style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>No hay gastos registrados aún.</td></tr>
                                ) : (
                                    gastos.map(g => (
                                        <tr key={g.id}>
                                            <td style={{ fontWeight: 600, color: '#475569' }}>
                                                {new Date(g.fecha_consumo).toLocaleDateString('es-PA', { timeZone: 'UTC' })}
                                            </td>
                                            <td style={{ fontWeight: 500 }}>{g.descripcion}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>
                                                ${parseFloat(g.monto).toFixed(2)}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <button
                                                    className="btn-cancel-ingreso-action"
                                                    style={{ padding: '4px 10px', fontSize: '11px' }}
                                                    onClick={() => handleDeleteGasto(g.id)}
                                                    title="Eliminar este gasto"
                                                >
                                                    🗑️ Eliminar
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                            {gastos.length > 0 && (
                                <tfoot>
                                    <tr>
                                        <td colSpan="2" style={{ textAlign: 'right', fontWeight: 700, padding: '10px 16px', color: '#0f172a' }}>TOTAL GASTOS:</td>
                                        <td style={{ textAlign: 'right', fontWeight: 800, fontSize: '15px', color: '#dc2626', padding: '10px 16px' }}>
                                            ${gastos.reduce((s, g) => s + parseFloat(g.monto), 0).toFixed(2)} USD
                                        </td>
                                        <td />
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════
                TAB: CIERRE QUINCENAL
                ═════════════════════════════════════════════════ */}
            {tab === 'cierre' && (
                <div style={{ padding: '20px 0' }}>
                    {cierreLoading ? (
                        <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Calculando cierres quincenales...</div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            {[cierreQ1, cierreQ2].map((q, idx) => {
                                if (!q) return (
                                    <div key={idx} style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '14px', padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                        Sin datos para la quincena {idx + 1}
                                    </div>
                                );
                                const ingresos = parseFloat(q.recaudado_periodo || 0);
                                const gastosTot = parseFloat(q.gastos_total || 0);
                                const saldo = ingresos - gastosTot;
                                return (
                                    <div key={idx} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                                        <div style={{ background: idx === 0 ? 'linear-gradient(135deg,#0f172a,#1e3a5f)' : 'linear-gradient(135deg,#1e3a5f,#0f4c75)', padding: '16px 20px' }}>
                                            <div style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>Quincena {idx + 1}</div>
                                            <div style={{ color: '#fff', fontSize: '15px', fontWeight: 800, marginTop: '4px' }}>{q.label}</div>
                                        </div>
                                        <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '12px' }}>
                                                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>💵 INGRESOS</div>
                                                <div style={{ fontSize: '20px', fontWeight: 800, color: '#059669', marginTop: '4px' }}>${ingresos.toFixed(2)}</div>
                                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{q.salidas_periodo || 0} cobros</div>
                                            </div>
                                            <div style={{ background: '#fff5f5', borderRadius: '8px', padding: '12px' }}>
                                                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>🧾 GASTOS</div>
                                                <div style={{ fontSize: '20px', fontWeight: 800, color: '#dc2626', marginTop: '4px' }}>${gastosTot.toFixed(2)}</div>
                                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{(q.gastos_detalle || []).length} egresos</div>
                                            </div>
                                            <div style={{ gridColumn: '1/-1', background: saldo >= 0 ? '#eff6ff' : '#fff5f5', borderRadius: '8px', padding: '14px', textAlign: 'center', border: `2px solid ${saldo >= 0 ? '#93c5fd' : '#fca5a5'}` }}>
                                                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>💰 SALDO NETO DE CAJA</div>
                                                <div style={{ fontSize: '26px', fontWeight: 900, color: saldo >= 0 ? '#1d4ed8' : '#b91c1c', marginTop: '6px' }}>
                                                    {saldo >= 0 ? '+' : ''}${saldo.toFixed(2)} USD
                                                </div>
                                            </div>
                                        </div>
                                        {(q.pagos_desglose || []).length > 0 && (
                                            <div style={{ padding: '0 20px 12px', borderTop: '1px solid #f1f5f9' }}>
                                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', margin: '12px 0 8px', textTransform: 'uppercase' }}>Desglose Cobros</div>
                                                {q.pagos_desglose.map((p, i) => (
                                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px dashed #f1f5f9' }}>
                                                        <span style={{ fontSize: '13px' }}>{p.metodo_pago === 'YAPPY' ? '🟣' : '💵'} {p.metodo_pago}</span>
                                                        <strong>${parseFloat(p.total).toFixed(2)}</strong>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {(q.gastos_detalle || []).length > 0 && (
                                            <div style={{ padding: '0 20px 16px' }}>
                                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', margin: '4px 0 8px', textTransform: 'uppercase' }}>Detalle Gastos</div>
                                                {q.gastos_detalle.map((g, i) => (
                                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px dashed #f1f5f9', fontSize: '12px' }}>
                                                        <span style={{ color: '#475569' }}>{new Date(g.fecha_consumo).toLocaleDateString('es-PA', { timeZone: 'UTC' })} — {g.descripcion}</span>
                                                        <span style={{ color: '#dc2626', fontWeight: 600 }}>${parseFloat(g.monto).toFixed(2)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                        <button className="btn-secondary-action" onClick={loadCierreQuincenal} disabled={cierreLoading}>
                            🔄 Actualizar Cierre
                        </button>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════
                MODAL CHECK-IN REDISEÑADO (ALTA CALIDAD VISUAL)
                ═════════════════════════════════════════════════ */}

            {/* ═════════════════════════════════════════════════
                MODAL EDITAR TICKET (FECHA INGRESO / RETORNO)
                ═════════════════════════════════════════════════ */}
            {isEditTicketOpen && ticketToEdit && (
                <div className="parking-modal-overlay">
                    <div className="parking-modal" style={{ maxWidth: '460px' }}>
                        <div className="modal-header-hero" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)' }}>
                            <div>
                                <h2>✏️ Editar Fecha del Ticket</h2>
                                <p>Corregir hora de ingreso o fecha estimada de retorno</p>
                            </div>
                            <button className="btn-close-modal-hero" onClick={() => setIsEditTicketOpen(false)}>✕</button>
                        </div>
                        <form onSubmit={handleConfirmEditTicket}>
                            <div className="modal-body-content">
                                <div className="checkout-receipt-box" style={{ marginBottom: '16px' }}>
                                    <div className="receipt-vehicle-row">
                                        <div>
                                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, display: 'block' }}>VEHÍCULO</span>
                                            <div className="license-plate-tag" style={{ marginTop: '4px' }}>
                                                <span className="plate-header-text">PANAMÁ</span>
                                                <span className="plate-number-text">{ticketToEdit.placa}</span>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, display: 'block' }}>CLIENTE</span>
                                            <span style={{ fontWeight: 800, fontSize: '14px' }}>{ticketToEdit.cliente_nombre}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-field-group">
                                    <label className="form-field-label">📅 Fecha y Hora de Ingreso *</label>
                                    <input
                                        type="datetime-local"
                                        className="form-field-input"
                                        value={editForm.fecha_ingreso}
                                        onChange={(e) => setEditForm({ ...editForm, fecha_ingreso: e.target.value })}
                                        required
                                    />
                                    <span style={{ fontSize: '11px', color: '#64748b' }}>El tiempo en patio se recalculará automáticamente.</span>
                                </div>

                                <div className="form-field-group">
                                    <label className="form-field-label">🗓️ Fecha Estimada de Retorno</label>
                                    <input
                                        type="date"
                                        className="form-field-input"
                                        value={editForm.fecha_estimada_retorno}
                                        onChange={(e) => setEditForm({ ...editForm, fecha_estimada_retorno: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer-actions">
                                <button type="button" className="btn-secondary-action" onClick={() => setIsEditTicketOpen(false)}>Cancelar</button>
                                <button type="submit" className="btn-primary-action" disabled={editSubmitting}
                                    style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)' }}
                                >
                                    {editSubmitting ? 'Guardando...' : '✅ Guardar Cambios'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isCheckInOpen && (
                <div className="parking-modal-overlay">
                    <div className="parking-modal">
                        <div className="modal-header-hero">
                            <div>
                                <h2>🚗 Nuevo Ingreso de Vehículo</h2>
                                <p>Registro de check-in y asignación de ticket de patio</p>
                            </div>
                            <button className="btn-close-modal-hero" onClick={() => setIsCheckInOpen(false)}>✕</button>
                        </div>
                        <form onSubmit={handleCheckInSubmit}>
                            <div className="modal-body-content">
                                {/* Placa Hero */}
                                <div className="plate-input-card">
                                    <div className="plate-hero-box">
                                        <span className="plate-hero-country">🇵🇦 REPÚBLICA DE PANAMÁ</span>
                                        <input 
                                            type="text" 
                                            className="plate-hero-input"
                                            placeholder="DD1453"
                                            required
                                            autoFocus
                                            value={checkInForm.placa}
                                            onChange={(e) => setCheckInForm({ ...checkInForm, placa: e.target.value.toUpperCase() })}
                                        />
                                    </div>
                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>Escribe la placa vehicular</span>
                                </div>

                                <div className="form-field-group">
                                    <label className="form-field-label">👤 Nombre y Apellidos del Cliente *</label>
                                    <input 
                                        type="text" 
                                        className="form-field-input"
                                        placeholder="Ej: Tomas Cussatti"
                                        required
                                        value={checkInForm.cliente_nombre}
                                        onChange={(e) => setCheckInForm({ ...checkInForm, cliente_nombre: e.target.value })}
                                    />
                                </div>

                                <div className="form-field-group">
                                    <label className="form-field-label">📱 Teléfono / WhatsApp</label>
                                    <input 
                                        type="text" 
                                        className="form-field-input"
                                        placeholder="Ej: 6203-2745 o +507..."
                                        value={checkInForm.telefono}
                                        onChange={(e) => setCheckInForm({ ...checkInForm, telefono: e.target.value })}
                                    />
                                </div>


                                <div className="form-field-group">
                                    <label className="form-field-label">📅 Fecha Estimada de Retorno</label>
                                    <input 
                                        type="date" 
                                        className="form-field-input"
                                        value={checkInForm.fecha_estimada_retorno}
                                        onChange={(e) => setCheckInForm({ ...checkInForm, fecha_estimada_retorno: e.target.value })}
                                    />
                                </div>

                                <div className="form-field-group">
                                    <label className="form-field-label">📝 Observaciones de Inspección</label>
                                    <textarea 
                                        className="form-field-input"
                                        rows="2"
                                        placeholder="Rayones previos, estado de las llaves, etc."
                                        value={checkInForm.observaciones}
                                        onChange={(e) => setCheckInForm({ ...checkInForm, observaciones: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer-actions">
                                <button type="button" className="btn-secondary-action" onClick={() => setIsCheckInOpen(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary-action" disabled={submitting}>
                                    {submitting ? 'Guardando...' : '📥 Confirmar Ingreso al Patio'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════
                MODAL CHECK-OUT Y COBRO REDISEÑADO
                ═════════════════════════════════════════════════ */}
            {isCheckOutOpen && (
                <div className="parking-modal-overlay">
                    <div className="parking-modal" style={{ maxWidth: '560px' }}>
                        <div className="modal-header-hero" style={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)' }}>
                            <div>
                                <h2>💳 Liquidación y Salida</h2>
                                <p>Cálculo de estadía y recepción de pago</p>
                            </div>
                            <button className="btn-close-modal-hero" onClick={() => setIsCheckOutOpen(false)}>✕</button>
                        </div>
                        {calcLoading ? (
                            <div style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
                                Calculando días exactos y tarifa escalonada...
                            </div>
                        ) : calcData ? (
                            <form onSubmit={handleConfirmCheckout}>
                                <div className="modal-body-content">
                                    {/* Recibo de Cobro */}
                                    <div className="checkout-receipt-box">
                                        <div className="receipt-vehicle-row">
                                            <div>
                                                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, display: 'block' }}>VEHÍCULO</span>
                                                <div className="license-plate-tag" style={{ marginTop: '4px' }}>
                                                    <span className="plate-header-text">PANAMÁ</span>
                                                    <span className="plate-number-text">{calcData.placa}</span>
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, display: 'block' }}>PROPIETARIO</span>
                                                <span style={{ fontWeight: 800, fontSize: '15px', color: '#0f172a' }}>{calcData.cliente_nombre}</span>
                                            </div>
                                        </div>

                                        <div className="receipt-metric-row">
                                            <span>Fecha de Ingreso:</span>
                                            <strong>{new Date(calcData.fecha_ingreso).toLocaleString()}</strong>
                                        </div>
                                        <div className="receipt-metric-row" style={{ alignItems: 'center' }}>
                                            <span>Fecha de Salida:</span>
                                            {['superadmin', 'client'].includes(JSON.parse(localStorage.getItem('user'))?.role) ? (
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    <input 
                                                        type="datetime-local" 
                                                        style={{ padding: '4px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '13px' }}
                                                        value={fechaSalidaManual}
                                                        onChange={(e) => setFechaSalidaManual(e.target.value)}
                                                    />
                                                    <button 
                                                        type="button" 
                                                        className="btn-secondary-action" 
                                                        style={{ padding: '4px 8px', fontSize: '12px' }}
                                                        onClick={() => fetchCalculoCheckout(calcData.ticket_id, new Date(fechaSalidaManual).toISOString())}
                                                    >
                                                        Recalcular
                                                    </button>
                                                </div>
                                            ) : (
                                                <strong>{new Date(calcData.fecha_salida_calculada).toLocaleString()}</strong>
                                            )}
                                        </div>
                                        <div className="receipt-metric-row">
                                            <span>Tiempo Transcurrido:</span>
                                            <strong style={{ color: '#0284c7' }}>{calcData.tiempo_transcurrido}</strong>
                                        </div>
                                        <div className="receipt-metric-row">
                                            <span>Días a Liquidar:</span>
                                            <strong style={{ fontSize: '15px' }}>{calcData.dias_a_cobrar} días</strong>
                                        </div>

                                        {calcData.aplico_tolerancia && (
                                            <div className="rule-grace-banner">
                                                <span>⚡</span>
                                                <div>
                                                    <strong>Regla de {calcData.horas_gracia || 6} Horas:</strong>{' '}
                                                    {calcData.dias_base > 0
                                                        ? `El exceso de ${calcData.horas_remanentes} horas superó las ${calcData.horas_gracia || 6}h de tolerancia, sumando 1 día completo extra.`
                                                        : `La estadía (${calcData.horas_remanentes}h ${calcData.minutos_remanentes || 0}m) superó las ${calcData.horas_gracia || 6}h de tolerancia, considerándose como 1 día completo.`
                                                    }
                                                </div>
                                            </div>
                                        )}

                                        {!calcData.aplico_tolerancia && calcData.dias_base === 0 && (
                                            <div style={{ fontSize: '12px', color: '#64748b', background: '#f8fafc', padding: '8px 12px', borderRadius: '6px', border: '1px dashed #cbd5e1', margin: '8px 0' }}>
                                                ℹ️ <strong>Estadía menor a {calcData.horas_gracia || 6} horas:</strong> No supera la tolerancia para liquidar como día completo. Puedes acordar y digitar un monto manual si corresponde.
                                            </div>
                                        )}

                                        {/* Tabla de tarifas escalonadas - desplegable */}
                                        <div className="tarifa-table-wrapper">
                                            <div
                                                className="tarifa-table-header tarifa-table-toggle"
                                                onClick={() => setShowTarifaTable(v => !v)}
                                            >
                                                <span className="tarifa-table-label">N° DE DÍAS FINAL</span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <span className="tarifa-dias-badge">{currentDias} días</span>
                                                    <span className="tarifa-chevron" style={{ transform: showTarifaTable ? 'rotate(180deg)' : 'rotate(0deg)' }}>&#9660;</span>
                                                </div>
                                            </div>
                                            {showTarifaTable && (
                                                <table className="tarifa-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Rango (días)</th>
                                                            <th>Tarifa/día</th>
                                                            <th>Tramo</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {TARIFAS.map((t) => {
                                                            const activo = currentDias >= t.min && currentDias <= t.max;
                                                            return (
                                                                <tr key={t.label} className={activo ? 'tarifa-row-active' : ''}>
                                                                    <td>{t.label}</td>
                                                                    <td>${t.precio.toFixed(2)}</td>
                                                                    <td>{activo ? <span className="tarifa-tramo-badge">✓ Aplicado</span> : ''}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>

                                        {/* Tarjeta de Monto Total a Cobrar (Editable por acuerdo de dueños / horas extras) */}
                                        <div className="checkout-monto-final-card">
                                            <div className="monto-final-header">
                                                <div>
                                                    <span className="monto-final-badge">TOTAL FINAL ACORDADO</span>
                                                    <label className="monto-final-label">Monto Total a Cobrar ($ USD) *</label>
                                                </div>
                                                <div className="monto-sugerido-ref">
                                                    <span>Tarifa sugerida:</span>
                                                    <strong>${totalCalculado.toFixed(2)} USD</strong>
                                                </div>
                                            </div>
                                            <div className="monto-final-input-box">
                                                <span className="currency-prefix">$</span>
                                                <input 
                                                    type="number" 
                                                    step="0.01" 
                                                    min="0.01"
                                                    className="monto-final-input"
                                                    value={montoTotalCobrar}
                                                    onChange={(e) => {
                                                        const nuevo = e.target.value;
                                                        setMontoTotalCobrar(nuevo);
                                                        if (metodoPago === 'EFECTIVO') {
                                                            setMontoEfectivo(nuevo);
                                                            setMontoYappy('');
                                                        } else if (metodoPago === 'YAPPY') {
                                                            setMontoYappy(nuevo);
                                                            setMontoEfectivo('');
                                                        }
                                                    }}
                                                    placeholder="0.00"
                                                    required
                                                />
                                                <span className="currency-suffix">USD</span>
                                            </div>
                                            <span className="monto-final-hint">
                                                💡 Puedes ajustar este monto si acordaron un valor especial por horas extras, estadía parcial o descuento pactado.
                                            </span>
                                        </div>
                                    </div>

                                    {/* Métodos de Pago Panamá */}
                                    <div className="form-field-group">
                                        <label className="form-field-label">💳 Forma de Pago en Panamá (USD)</label>

                                        {/* Selector de método */}
                                        <div className="payment-method-selector">
                                            <button
                                                type="button"
                                                className={`pay-method-btn pay-method-cash ${metodoPago === 'EFECTIVO' ? 'active' : ''}`}
                                                onClick={() => {
                                                    setMetodoPago('EFECTIVO');
                                                    setMontoEfectivo(montoTotalCobrar);
                                                    setMontoYappy('');
                                                }}
                                            >
                                                <span className="pay-method-icon">💵</span>
                                                <span className="pay-method-label">Efectivo</span>
                                            </button>
                                            <button
                                                type="button"
                                                className={`pay-method-btn pay-method-yappy ${metodoPago === 'YAPPY' ? 'active' : ''}`}
                                                onClick={() => {
                                                    setMetodoPago('YAPPY');
                                                    setMontoYappy(montoTotalCobrar);
                                                    setMontoEfectivo('');
                                                }}
                                            >
                                                <span className="pay-method-icon">🟣</span>
                                                <span className="pay-method-label">Yappy</span>
                                            </button>
                                        </div>

                                        {/* Panel Efectivo */}
                                        {metodoPago === 'EFECTIVO' && (
                                            <div className="pay-card pay-card-cash pay-card-active">
                                                <div className="pay-card-header">
                                                    <span className="pay-card-title" style={{ color: '#059669' }}>💵 EFECTIVO</span>
                                                    <button type="button" className="btn-fill-pill" onClick={llenarTodoEfectivo}>
                                                        Pagar Total
                                                    </button>
                                                </div>
                                                <input 
                                                    type="number" 
                                                    step="0.01" 
                                                    placeholder="Monto $"
                                                    className="form-field-input"
                                                    value={montoEfectivo}
                                                    onChange={(e) => setMontoEfectivo(e.target.value)}
                                                    autoFocus
                                                />
                                            </div>
                                        )}

                                        {/* Panel Yappy */}
                                        {metodoPago === 'YAPPY' && (
                                            <div className="pay-card pay-card-yappy pay-card-active">
                                                <div className="pay-card-header">
                                                    <span className="pay-card-title" style={{ color: '#7c3aed' }}>🟣 YAPPY</span>
                                                    <button type="button" className="btn-fill-pill" onClick={llenarTodoYappy}>
                                                        Pagar Total
                                                    </button>
                                                </div>
                                                <input 
                                                    type="number" 
                                                    step="0.01" 
                                                    placeholder="Monto $"
                                                    className="form-field-input"
                                                    value={montoYappy}
                                                    onChange={(e) => setMontoYappy(e.target.value)}
                                                    autoFocus
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Opción de destino: Salida Inmediata vs Pago Anticipado en Patio */}
                                    <div className="checkout-tipo-salida-card">
                                        <label className="form-field-label">🚗 ¿Cuándo se retira el vehículo del patio?</label>
                                        <div className="tipo-salida-options">
                                            <button
                                                type="button"
                                                className={`tipo-salida-btn ${!esPagoAnticipado ? 'active' : ''}`}
                                                onClick={() => setEsPagoAnticipado(false)}
                                            >
                                                <span className="tipo-salida-icon">🚪</span>
                                                <div className="tipo-salida-text">
                                                    <strong>Salida Inmediata</strong>
                                                    <span>El cliente retira el auto en este momento</span>
                                                </div>
                                            </button>
                                            <button
                                                type="button"
                                                className={`tipo-salida-btn prepago ${esPagoAnticipado ? 'active' : ''}`}
                                                onClick={() => setEsPagoAnticipado(true)}
                                            >
                                                <span className="tipo-salida-icon">🅿️</span>
                                                <div className="tipo-salida-text">
                                                    <strong>Pago Anticipado (Queda en Patio)</strong>
                                                    <span>El cliente pagó por adelantado y se va de viaje</span>
                                                </div>
                                            </button>
                                        </div>
                                        {esPagoAnticipado && (
                                            <div className="prepago-notice-banner">
                                                <span className="prepago-notice-icon">🟢</span>
                                                <div>
                                                    <strong>El vehículo permanecerá en Patio:</strong> El cobro ingresará hoy a caja (${(parseFloat(montoTotalCobrar) || 0).toFixed(2)} USD), pero el vehículo <strong>se mantendrá en la lista de Patio</strong> con estado <em>"🟢 Pagado (Prepago)"</em>. Al volver de su viaje, solo das clic en "Dar Salida".
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-field-group" style={{ marginTop: '12px' }}>
                                        <label className="form-field-label">Observaciones de Entrega o Prepago</label>
                                        <input 
                                            type="text" 
                                            className="form-field-input"
                                            placeholder="Detalles adicionales..."
                                            value={obsCheckout}
                                            onChange={(e) => setObsCheckout(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer-actions">
                                    <button type="button" className="btn-secondary-action" onClick={() => setIsCheckOutOpen(false)}>
                                        Cancelar
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn-primary-action" 
                                        style={{
                                            background: esPagoAnticipado 
                                                ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' 
                                                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                            boxShadow: esPagoAnticipado 
                                                ? '0 4px 14px rgba(5, 150, 105, 0.4)' 
                                                : '0 4px 14px rgba(16, 185, 129, 0.35)'
                                        }}
                                        disabled={submitting}
                                    >
                                        {submitting 
                                            ? 'Registrando...' 
                                            : esPagoAnticipado
                                            ? `🅿️ Registrar Prepago $${(parseFloat(montoTotalCobrar) || 0).toFixed(2)} (Queda en Patio)`
                                            : `✅ Cobrar $${(parseFloat(montoTotalCobrar) || 0).toFixed(2)} y Dar Salida`}
                                    </button>
                                </div>
                            </form>
                        ) : null}
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════
                MODAL CANCELAR INGRESO (CHECK-IN)
                ═════════════════════════════════════════════════ */}
            {isCancelIngresoOpen && ticketToCancel && (
                <div className="parking-modal-overlay">
                    <div className="parking-modal" style={{ maxWidth: '480px' }}>
                        <div className="modal-header-hero" style={{ background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)' }}>
                            <div>
                                <h2>✕ Cancelar Ingreso a Patio</h2>
                                <p>El vehículo se marcará como cancelado y no generará cobro</p>
                            </div>
                            <button className="btn-close-modal-hero" onClick={() => setIsCancelIngresoOpen(false)}>✕</button>
                        </div>
                        <form onSubmit={handleConfirmCancelIngreso}>
                            <div className="modal-body-content">
                                <div className="checkout-receipt-box" style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
                                    <div className="receipt-vehicle-row">
                                        <div>
                                            <span style={{ fontSize: '11px', color: '#991b1b', fontWeight: 700, display: 'block' }}>VEHÍCULO</span>
                                            <div className="license-plate-tag" style={{ marginTop: '4px' }}>
                                                <span className="plate-header-text">PANAMÁ</span>
                                                <span className="plate-number-text">{ticketToCancel.placa}</span>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span style={{ fontSize: '11px', color: '#991b1b', fontWeight: 700, display: 'block' }}>TICKET / CLIENTE</span>
                                            <span style={{ fontWeight: 800, fontSize: '14px', color: '#0f172a' }}>{ticketToCancel.cliente_nombre}</span>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>{ticketToCancel.codigo_ticket}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-field-group">
                                    <label className="form-field-label">Motivo de la cancelación *</label>
                                    <textarea 
                                        className="form-field-input"
                                        rows="3"
                                        required
                                        placeholder="Ej: Placa errónea, cliente se retiró antes de viajar..."
                                        value={cancelIngresoMotivo}
                                        onChange={(e) => setCancelIngresoMotivo(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <div className="modal-footer-actions">
                                <button type="button" className="btn-secondary-action" onClick={() => setIsCancelIngresoOpen(false)}>
                                    Volver
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn-primary-action" 
                                    style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)', boxShadow: '0 4px 14px rgba(220, 38, 38, 0.35)' }}
                                    disabled={anularSubmitting}
                                >
                                    {anularSubmitting ? 'Cancelando...' : 'Confirmar Cancelación'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════
                MODAL ANULAR COBRO / VENTA
                ═════════════════════════════════════════════════ */}
            {isAnularCobroOpen && ticketToAnularCobro && (
                <div className="parking-modal-overlay">
                    <div className="parking-modal" style={{ maxWidth: '520px' }}>
                        <div className="modal-header-hero" style={{ background: 'linear-gradient(135deg, #831843 0%, #9f1239 100%)' }}>
                            <div>
                                <h2>🚫 Anular Cobro / Venta de Parking</h2>
                                <p>Revertir liquidación o anular venta registrada</p>
                            </div>
                            <button className="btn-close-modal-hero" onClick={() => setIsAnularCobroOpen(false)}>✕</button>
                        </div>
                        <form onSubmit={handleConfirmAnularCobro}>
                            <div className="modal-body-content">
                                <div className="checkout-receipt-box" style={{ background: '#fff1f2', borderColor: '#fecdd3' }}>
                                    <div className="receipt-vehicle-row">
                                        <div>
                                            <span style={{ fontSize: '11px', color: '#9f1239', fontWeight: 700, display: 'block' }}>TICKET</span>
                                            <strong style={{ fontSize: '15px' }}>{ticketToAnularCobro.codigo_ticket}</strong>
                                            <div style={{ marginTop: '3px' }}>
                                                <span className="plate-number-text" style={{ fontWeight: 800 }}>{ticketToAnularCobro.placa}</span>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span style={{ fontSize: '11px', color: '#9f1239', fontWeight: 700, display: 'block' }}>MONTO COBRADO</span>
                                            <span style={{ fontWeight: 900, fontSize: '18px', color: '#b91c1c' }}>
                                                ${parseFloat(ticketToAnularCobro.monto_total || 0).toFixed(2)} USD
                                            </span>
                                            <div style={{ fontSize: '11px', color: '#64748b' }}>{ticketToAnularCobro.cliente_nombre}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-field-group">
                                    <label className="form-field-label">¿Qué acción deseas realizar?</label>
                                    <div className="anular-action-selector">
                                        <label 
                                            className={`anular-radio-card ${anularCobroAccion === 'REABRIR' ? 'active' : ''}`}
                                            onClick={() => setAnularCobroAccion('REABRIR')}
                                        >
                                            <input 
                                                type="radio" 
                                                name="anularAccion" 
                                                value="REABRIR" 
                                                checked={anularCobroAccion === 'REABRIR'} 
                                                onChange={() => setAnularCobroAccion('REABRIR')} 
                                            />
                                            <div className="anular-card-body">
                                                <span className="anular-card-title">🔄 Reabrir Ticket y Regresar a Patio</span>
                                                <span className="anular-card-desc">
                                                    Devuelve el vehículo a la lista de "En Patio", borra los pagos registrados y permite volver a cobrarlo correctamente (ideal si se equivocaron de método de pago o tarifa).
                                                </span>
                                            </div>
                                        </label>

                                        <label 
                                            className={`anular-radio-card danger ${anularCobroAccion === 'DEFINITIVA' ? 'active' : ''}`}
                                            onClick={() => setAnularCobroAccion('DEFINITIVA')}
                                        >
                                            <input 
                                                type="radio" 
                                                name="anularAccion" 
                                                value="DEFINITIVA" 
                                                checked={anularCobroAccion === 'DEFINITIVA'} 
                                                onChange={() => setAnularCobroAccion('DEFINITIVA')} 
                                            />
                                            <div className="anular-card-body">
                                                <span className="anular-card-title" style={{ color: '#b91c1c' }}>🛑 Anulación Definitiva de Venta</span>
                                                <span className="anular-card-desc">
                                                    Marca la venta como anulada permanentemente. El dinero se resta del cuadre de caja de hoy y el ticket no vuelve a patio.
                                                </span>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="form-field-group">
                                    <label className="form-field-label">Motivo de la anulación *</label>
                                    <textarea 
                                        className="form-field-input"
                                        rows="2"
                                        required
                                        placeholder="Ej: Se cobró en efectivo pero el cliente pagó por Yappy, o cobro duplicado..."
                                        value={anularCobroMotivo}
                                        onChange={(e) => setAnularCobroMotivo(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <div className="modal-footer-actions">
                                <button type="button" className="btn-secondary-action" onClick={() => setIsAnularCobroOpen(false)}>
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn-primary-action" 
                                    style={{ 
                                        background: anularCobroAccion === 'REABRIR' 
                                            ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' 
                                            : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                        boxShadow: anularCobroAccion === 'REABRIR'
                                            ? '0 4px 14px rgba(37, 99, 235, 0.35)'
                                            : '0 4px 14px rgba(220, 38, 38, 0.35)'
                                    }}
                                    disabled={anularSubmitting}
                                >
                                    {anularSubmitting ? 'Procesando...' : (
                                        anularCobroAccion === 'REABRIR' 
                                            ? '🔄 Confirmar y Devolver a Patio' 
                                            : '🛑 Confirmar Anulación Definitiva'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════
                MODAL DAR SALIDA (VEHÍCULOS PREPAGADOS)
                ═════════════════════════════════════════════════ */}
            {isDarSalidaOpen && ticketToDarSalida && (
                <div className="parking-modal-overlay">
                    <div className="parking-modal" style={{ maxWidth: '500px' }}>
                        <div className="modal-header-hero" style={{ background: 'linear-gradient(135deg, #065f46 0%, #047857 100%)' }}>
                            <div>
                                <h2>🚪 Dar Salida al Vehículo</h2>
                                <p>El cliente ya pagó por adelantado y retira su automóvil</p>
                            </div>
                            <button className="btn-close-modal-hero" onClick={() => setIsDarSalidaOpen(false)}>✕</button>
                        </div>
                        <form onSubmit={handleConfirmDarSalida}>
                            <div className="modal-body-content">
                                <div className="checkout-receipt-box" style={{ background: '#ecfdf5', borderColor: '#a7f3d0' }}>
                                    <div className="receipt-vehicle-row">
                                        <div>
                                            <span style={{ fontSize: '11px', color: '#065f46', fontWeight: 700, display: 'block' }}>VEHÍCULO</span>
                                            <div className="license-plate-tag" style={{ marginTop: '4px' }}>
                                                <span className="plate-header-text">PANAMÁ</span>
                                                <span className="plate-number-text">{ticketToDarSalida.placa}</span>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span style={{ fontSize: '11px', color: '#065f46', fontWeight: 700, display: 'block' }}>CLIENTE</span>
                                            <span style={{ fontWeight: 800, fontSize: '15px', color: '#0f172a' }}>{ticketToDarSalida.cliente_nombre}</span>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>{ticketToDarSalida.codigo_ticket}</div>
                                        </div>
                                    </div>
                                    <div className="receipt-metric-row" style={{ marginTop: '12px', borderTop: '1px dashed #a7f3d0', paddingTop: '10px' }}>
                                        <span>Total Pagado:</span>
                                        <strong style={{ color: '#059669', fontSize: '16px' }}>
                                            ${parseFloat(ticketToDarSalida.total_pagado || ticketToDarSalida.monto_total || 0).toFixed(2)} USD (PAGADO)
                                        </strong>
                                    </div>
                                    <div className="receipt-metric-row">
                                        <span>Días Prepagados:</span>
                                        <strong>{ticketToDarSalida.dias_cobrados || 1} días</strong>
                                    </div>
                                    <div className="receipt-metric-row">
                                        <span>Tiempo Transcurrido:</span>
                                        <strong style={{ color: '#0284c7' }}>{ticketToDarSalida.tiempo_transcurrido_texto}</strong>
                                    </div>
                                    {ticketToDarSalida.dias_excedentes > 0 && (
                                        <div className="rule-grace-banner" style={{ background: '#fef3c7', borderColor: '#fde68a', color: '#92400e', marginTop: '10px' }}>
                                            <span>⚠️</span>
                                            <div>
                                                <strong>Excedió estadía:</strong> El vehículo superó en {ticketToDarSalida.dias_excedentes} día(s) lo prepagado. Si deseas cobrar los días extra antes de la salida, cierra este diálogo y usa el botón "Cobrar Extra".
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="form-field-group">
                                    <label className="form-field-label">Observaciones de salida (opcional)</label>
                                    <input 
                                        type="text" 
                                        className="form-field-input"
                                        placeholder="Ej: Retirado por el titular sin novedades..."
                                        value={darSalidaObs}
                                        onChange={(e) => setDarSalidaObs(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <div className="modal-footer-actions">
                                <button type="button" className="btn-secondary-action" onClick={() => setIsDarSalidaOpen(false)}>
                                    Volver
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn-primary-action" 
                                    style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', boxShadow: '0 4px 14px rgba(5, 150, 105, 0.35)' }}
                                    disabled={darSalidaSubmitting}
                                >
                                    {darSalidaSubmitting ? 'Registrando salida...' : '🚪 Confirmar Salida del Vehículo'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
